import { useStreamingGatewayUrl, useStreamingGatewayAuthToken } from './streamingGatewayApi';
import { LlmConversation } from './llmConversationsService';
import { useTimezoneService } from './timezoneService';

export enum ChatMessageChunkType {
  CONVERSATION_CREATED = 'conversation-created',
  TEXT_DELTA = 'text-delta',
  TOOL_CALL = 'tool-call',
  TOOL_RESULT = 'tool-result',
  FINISH = 'finish',
  ERROR = 'error',
}

export interface ChatMessageChunk {
  type: ChatMessageChunkType;
  text?: string;
  conversation?: LlmConversation;
  usage?: {
    inputTokens: number;
    outputTokens: number;
  };
  error?: string;
  toolCallId?: string;
  toolName?: string;
  displayMessage?: string | null;
}

export type OnChunkCallback = (chunk: ChatMessageChunk) => void;
export type AbortStreamFunction = () => void;

async function streamNdjson(
  url: string,
  body: object,
  token: string,
  timezone: string,
  onChunk: OnChunkCallback,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-NEKST-TIMEZONE': timezone,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  if (!response.body) {
    throw new Error('Response body is not available');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;

      if (!done && result.value) {
        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim()) {
            try {
              const chunk = JSON.parse(line) as ChatMessageChunk;
              onChunk(chunk);

              if (chunk.type === ChatMessageChunkType.ERROR) {
                throw new Error(chunk.error || 'Stream error');
              }
            } catch (parseError) {
              if (parseError instanceof SyntaxError) {
                console.error('Failed to parse NDJSON line:', line);
              } else {
                throw parseError;
              }
            }
          }
        }
      }
    }

    if (buffer.trim()) {
      try {
        const chunk = JSON.parse(buffer) as ChatMessageChunk;
        onChunk(chunk);
      } catch {
        console.error('Failed to parse remaining buffer:', buffer);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export interface StartConversationRequest {
  message: string;
  model?: string;
}

export interface SendMessageRequest {
  message: string;
}

export function useLlmChatService() {
  const streamingGatewayUrl = useStreamingGatewayUrl();
  const authToken = useStreamingGatewayAuthToken();
  const timezoneService = useTimezoneService();

  const startConversation = (
    request: StartConversationRequest,
    onChunk: OnChunkCallback,
  ): AbortStreamFunction => {
    const controller = new AbortController();

    streamNdjson(
      `${streamingGatewayUrl}/chats`,
      request,
      authToken,
      timezoneService.getTimezone(),
      onChunk,
      controller.signal,
    ).catch((error) => {
      if (error.name !== 'AbortError') {
        onChunk({
          type: ChatMessageChunkType.ERROR,
          error: error.message || 'Unknown error occurred',
        });
      }
    });

    return () => controller.abort();
  };

  const sendMessage = (
    conversationId: number,
    request: SendMessageRequest,
    onChunk: OnChunkCallback,
  ): AbortStreamFunction => {
    const controller = new AbortController();

    streamNdjson(
      `${streamingGatewayUrl}/chats/${conversationId}/message`,
      request,
      authToken,
      timezoneService.getTimezone(),
      onChunk,
      controller.signal,
    ).catch((error) => {
      if (error.name !== 'AbortError') {
        onChunk({
          type: ChatMessageChunkType.ERROR,
          error: error.message || 'Unknown error occurred',
        });
      }
    });

    return () => controller.abort();
  };

  return {
    startConversation,
    sendMessage,
  };
}
